import { useAiEndpoints } from '~/composables/useAiEndpoints';

const WS_CONNECT_TIMEOUT_MS = 5000;
const KEEP_ALIVE_INTERVAL_MS = 8000;
const IDLE_TIMEOUT_MS = 2 * 60 * 1000;

export const useDeepgramSocket = () => {
    const { url, getHeaders } = useAiEndpoints();

    let ws: WebSocket | null = null;
    let keepAliveId: ReturnType<typeof setInterval> | null = null;
    let idleTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const isWsOpen = (): boolean => ws !== null && ws.readyState === WebSocket.OPEN;

    const getWs = (): WebSocket | null => ws;

    const connectWebSocket = (url: string, token: string): Promise<WebSocket> => {
        return new Promise((resolve, reject) => {
            const socket = new WebSocket(url, ['token', token]);
            const timeout = setTimeout(() => {
                socket.close();
                reject(new Error('WebSocket connection timeout'));
            }, WS_CONNECT_TIMEOUT_MS);

            socket.onopen = () => {
                clearTimeout(timeout);
                resolve(socket);
            };
            socket.onerror = () => {
                clearTimeout(timeout);
                reject(new Error('WebSocket connection failed'));
            };
        });
    };

    const startKeepAlive = () => {
        stopKeepAlive();
        keepAliveId = setInterval(() => {
            if (isWsOpen()) {
                ws!.send(JSON.stringify({ type: 'KeepAlive' }));
            }
        }, KEEP_ALIVE_INTERVAL_MS);
    };

    const stopKeepAlive = () => {
        if (keepAliveId) {
            clearInterval(keepAliveId);
            keepAliveId = null;
        }
    };

    const resetIdleTimeout = (onIdle: () => void) => {
        clearIdleTimeout();
        idleTimeoutId = setTimeout(onIdle, IDLE_TIMEOUT_MS);
    };

    const clearIdleTimeout = () => {
        if (idleTimeoutId) {
            clearTimeout(idleTimeoutId);
            idleTimeoutId = null;
        }
    };

    const fetchToken = async (): Promise<{ token: string; url: string } | null> => {
        // Deepgram token endpoint disabled until scoped temporary keys are implemented (B-1)
        console.warn('[Deepgram] Token endpoint disabled — speech recognition unavailable');
        return null;
    };

    const preconnect = async (onDisconnect: () => void) => {
        if (isWsOpen()) return;

        const tokenData = await fetchToken();
        if (!tokenData?.token || !tokenData?.url) return;

        try {
            ws = await connectWebSocket(tokenData.url, tokenData.token);
            startKeepAlive();
            resetIdleTimeout(onDisconnect);

            ws.onclose = () => {
                stopKeepAlive();
                ws = null;
            };
        } catch {
            ws = null;
        }
    };

    const ensureConnected = async (): Promise<boolean> => {
        if (isWsOpen()) return true;

        // Clean up any dead socket before reconnecting
        if (ws) {
            ws = null;
        }

        const tokenData = await fetchToken();
        if (!tokenData?.token || !tokenData?.url) return false;

        try {
            ws = await connectWebSocket(tokenData.url, tokenData.token);
            return true;
        } catch {
            ws = null;
            return false;
        }
    };

    const disconnect = () => {
        stopKeepAlive();
        clearIdleTimeout();

        if (ws) {
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'CloseStream' }));
                ws.close();
            }
            ws = null;
        }
    };

    /** Disconnect current socket and preconnect a fresh one for instant next recording. */
    const reconnect = () => {
        disconnect();
        preconnect(() => disconnect());
    };

    const sendFinalize = () => {
        if (isWsOpen()) {
            ws!.send(JSON.stringify({ type: 'Finalize' }));
        }
    };

    /** Restore proper onclose handler after streaming overrides it. */
    const restoreCloseHandler = () => {
        if (ws) {
            // If the socket already closed during streaming (Deepgram closes after Finalize),
            // clean up immediately instead of waiting for an onclose that already fired.
            if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
                stopKeepAlive();
                ws = null;
                return;
            }

            ws.onclose = () => {
                stopKeepAlive();
                ws = null;
            };
            ws.onerror = () => {};
            startKeepAlive();
            resetIdleTimeout(() => disconnect());
        }
    };

    return {
        getWs,
        isWsOpen,
        preconnect,
        ensureConnected,
        disconnect,
        stopKeepAlive,
        clearIdleTimeout,
        sendFinalize,
        restoreCloseHandler,
        reconnect,
    };
};
